import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { App } from '@df/core/app';
import { MultisiteService } from '@df/core/service/multisite';
import { WINDOW } from '@df/dom/globals';
import { NCore } from '../df-core.interfaces';

export interface IPathRule {
    from: RegExp;
    to: string;
    lowerCaseExt?: boolean;
}

/**
 * Register service in app
 */
declare module '@df/core/app' {
    interface App {
        url: Url;
    }
}

@Injectable({
    providedIn: 'root'
})
export class Url {
    private window = inject(WINDOW);

    private multisiteService = inject(MultisiteService);
    private app = inject(App);
    private location = inject(Location);
    private moduleConfig = inject(NCore.OPTIONS);

    readonly storeUrl = this.window.location.protocol + '//' + this.window.location.host + this.multisiteService.base;

    constructor() {
        this.app.url = this;
    }

    /**
     * Converts path to full media url
     */
    toMediaUrl(input: string, host: string = this.app.dfConfig.endpoint.media): string {
        if (!input || typeof input !== 'string') {
            return input;
        }

        if (input.match(/^http/i)) {
            return input;
        }

        const pathRules: IPathRule[] = [
            ...this.moduleConfig.url.pathRules,
            {
                from: /^\/cms\//,
                to: '/static/cms/',
                lowerCaseExt: true
            },
            {
                from: /^\/catalog\//,
                to: '/static/media/catalog/'
            },
            {
                from: /^\/product\//,
                to: '/static/media/catalog/product/'
            },
            {
                from: /^\/buffer\//,
                to: '/static/media/buffer/'
            },
            {
                from: /^\/swatch\//,
                to: '/static/media/attribute/swatch/'
            },
            {
                from: /^\/category\//,
                to: '/static/media/catalog/category/'
            },
            {
                from: /^\/storepickup\//,
                to: '/static/media/magestore/storepickup/images/store/gallery/'
            },
            {
                from: /^(\/*)magestore\/storepickup\/images\/tag\//,
                to: '/static/media/magestore/storepickup/images/tag/'
            },
            {
                from: /^(\/*)magenest\/giftregistry\//,
                to: '/static/media/magenest/giftregistry/'
            },
            {
                from: /^\/media\//,
                to: '/static/'
            },
            {
                from: /^\/front\//,
                to: '/images/'
            },
            {
                from: /^\/quote\//,
                to: '/static/media/quote/'
            },
            {
                from: /^\/common\//,
                to: '/assets/images-common/'
            },
            {
                from: /^\/hub\//,
                to: '/static/media/hub/'
            }
        ];

        const rule = pathRules.find(i => i.from.test(input));

        if (rule) {
            input = input.replace(rule.from, rule.to);
            if (rule.lowerCaseExt) {
                input = input.replace(/\.([a-z0-9]+)$/i, ext => ext.toLowerCase());
            }
        }

        return host + '/' + input.replace(/^\//, '');
    }

    /**
     * Provieds absolute url
     * if host is not set then current store is used
     * if host is string then its considered as host part
     * if host is number then its considered as any store id
     *
     */
    toUrl(input: string, host: string | number = this.storeUrl): string {
        if (input === undefined) {
            return input;
        }

        if (input.includes(':')) {
            return input;
        }

        if (typeof host === 'number') {
            const site = this.multisiteService.toMultisiteItem(host);

            if (site) {
                host = this.window.location.protocol + '//' + (site.domain || this.window.location.host) + (site.base || '/');
            } else {
                return '';
            }
        }
        host = host.replace(/\/$/, '') + '/';
        return host + input.replace(/^\/+/, '');
    }

    /**
     * Gets current path (including search params)
     */
    getPath(): string {
        return this.location.path().replace(new RegExp('^' + this.multisiteService.base), '/');
    }

    getSearch(prefix = true): string | undefined {
        const search = this.getPath().split('?')[1];
        return prefix && search ? '?' + search : search;
    }
    /**
     * Retrives search http params
     */
    getSearchParams() {
        return new HttpParams({ fromString: this.getSearch(false) });
    }
}
